import { api } from 'api';
import { CurrencyCodes } from 'common/configs/currency';
import { confirmInDangerousDev } from 'common/utils/dev';
import { requestErrorAction } from 'common/utils/requestErrorHandling';
import { PaginatedResponse, PaginationParams } from '../types';
import {
  Account,
  AccountAllocation,
  AccountBalance,
  AccountCustomer,
  AccountMovement,
  AccountOperating,
  AccountOwed,
  AccountTypes,
  LoginInfo,
  Xrates,
} from './actions';

// #region Account
export const requestUserAccounts = async (
  user: string | number = 'self',
  params?: PaginationParams
) =>
  await api.get<PaginatedResponse<Account>>(`/contact/${user}/account/`, {
    params,
  });

export const doGetUserAccounts =
  (user: string | number = 'self', params?: PaginationParams) =>
  async (dispatch) => {
    dispatch({ type: 'accounts/user/started' });

    try {
      const response = await requestUserAccounts(user, params);

      dispatch({
        type: 'accounts/user/finished',
        accounts: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'account/user/finished' },
          e,
          `Failed to get user "${user}" accounts.`
        )
      );
    }
  };

export interface AccountsParams extends PaginationParams {
  type?: AccountTypes;
  is_agent?: boolean;
  currency?: CurrencyCodes;
  system?: string;
}

export const requestAccounts = async (params?: PaginationParams) =>
  await api.get<PaginatedResponse<Account>>('/account/', {
    params,
  });

export const doGetAccounts =
  (params?: PaginationParams) => async (dispatch) => {
    dispatch({ type: 'accounts/started' });

    try {
      const response = await requestAccounts(params);

      dispatch({
        type: 'accounts/finished',
        accounts: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'accounts/finished' },
          e,
          `Failed to get accounts.`
        )
      );
    }
  };

export const requestLoginInfo = async (account: Account) =>
  await api
    .get<LoginInfo>(
      `/account/${account.system}/${account.username}/login_info/`
    )
    .then((response) => response.data);
// #endregion

// #region AccountBalance
export const requestUserAccountBalances = async (
  user: string | number = 'self',
  params?: PaginationParams
) =>
  await api.get<PaginatedResponse<AccountBalance>>(
    `/contact/${user}/account/balance/`,
    {
      params,
    }
  );

export const doGetUserAccountBalances =
  (user: string | number = 'self', params?: PaginationParams) =>
  async (dispatch) => {
    dispatch({ type: 'accounts/balances/user/started' });

    try {
      const response = await requestUserAccountBalances(user, params);

      dispatch({
        type: 'accounts/balances/user/finished',
        balances: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'accounts/balances/user/finished' },
          e,
          `Failed to get user "${user}" account balances.`
        )
      );
    }
  };
// #endregion

// #region AccountBalance
export const doGetUserAccountsWithBalance =
  (user: string | number = 'self', params?: PaginationParams) =>
  async (dispatch) => {
    dispatch({ type: 'accounts_with_balance/user/started' });

    try {
      const [accounts, balances] = await Promise.all([
        requestUserAccounts(user, params),
        requestUserAccountBalances(user, params),
      ]);

      dispatch({
        type: 'accounts_with_balance/user/finished',
        accounts: accounts.data.results.map((account, index) => ({
          ...balances.data.results[index],
          ...account,
        })),
        count: accounts.data.count,
        next: accounts.data.next,
        previous: accounts.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'accounts_with_balance/user/finished' },
          e,
          `Failed to get user "${user}" accounts with balance.`
        )
      );
    }
  };
// #endregion

// #region AccountOperating
export const requestAccountOperatings = async () =>
  await api.get<AccountOperating>('/weekops/account/operating/');

export const doGetAccountOperatings = () => async (dispatch) => {
  dispatch({ type: 'accounts/operating/started' });

  try {
    const response = await requestAccountOperatings();

    dispatch({
      type: 'accounts/operating/finished',
      operatings: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'accounts/operating/finished' },
        e,
        `Failed to get account operatings.`
      )
    );
  }
};
// #endregion

// #region AccountOwed
export const requestAccountOweds = async () =>
  await api.get<AccountOwed>('/weekops/account/owed/');

export const doGetAccountOweds = () => async (dispatch) => {
  dispatch({ type: 'accounts/owed/started' });

  try {
    const response = await requestAccountOweds();

    dispatch({
      type: 'accounts/owed/finished',
      oweds: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'accounts/owed/finished' },
        e,
        `Failed to get account oweds.`
      )
    );
  }
};
// #endregion

// #region AccountAllocation
export const requestAccountAllocations = async (
  accountId: number,
  params: PaginationParams
) =>
  await api.get<PaginatedResponse<AccountAllocation>>(
    `/account/${accountId}/allocation/`,
    {
      params,
    }
  );
// #endregion

// #region Xrates
export const requestXrates = async (
  currency: CurrencyCodes = CurrencyCodes.GBP
) =>
  await api.get<{ base: CurrencyCodes; date: string; rates: Xrates }>(
    `/currency/${currency}/xrates/`
  );

export const doGetXrates =
  (currency: CurrencyCodes = CurrencyCodes.GBP) =>
  async (dispatch) => {
    dispatch({ type: 'xrates/started' });

    try {
      const response = await requestXrates(currency);

      dispatch({
        type: 'xrates/finished',
        xrates: response.data.rates,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'xrates/finished' },
          e,
          `Failed to get xrates.`
        )
      );
    }
  };
// #endregion

// #region AccountCustomers
export const requestAccountCustomers = async () =>
  await api.get<AccountCustomer[]>('/weekops/account/client/customer/');

export const doGetAccountCustomers = () => async (dispatch) => {
  dispatch({ type: 'accounts/customers/started' });

  try {
    const response = await requestAccountCustomers();

    dispatch({
      type: 'accounts/customers/finished',
      customers: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'accounts/customers/finished' },
        e,
        `Failed to get account customers.`
      )
    );
  }
};
// #endregion

// #region AccountMovements
export const requestAccountMovements = async (
  accountCode: string,
  params?: PaginationParams
) =>
  await api.get<PaginatedResponse<AccountMovement>>(
    `/xact/account/${accountCode}/movements/`,
    {
      params,
    }
  );

export const doGetAccountMovements =
  (accountCode: string, params?: PaginationParams) => async (dispatch) => {
    dispatch({ type: 'accounts/movements/started' });

    try {
      const response = await requestAccountMovements(accountCode, params);

      dispatch({
        type: 'accounts/movements/finished',
        movements: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'accounts/movements/finished' },
          e,
          `Failed to get account "${accountCode}" account movements.`
        )
      );
    }
  };
// #endregion

// #region AdjustAccount
export enum AdjustmentTypes {
  ADJUSTMENT = 'ADJUST',
  ADJUSTING_TRANSFER = 'ADJUSTING_TRANSFER',
}

export interface AdjustAccountBodyData {
  post_date: string;
  amount: string;
  description: string;
  notes?: string;
  type: AdjustmentTypes;
}

export const requestAdjustAccount = async (
  clientCode: string,
  data: AdjustAccountBodyData
) =>
  confirmInDangerousDev(
    async () => await api.post(`/xact/account/${clientCode}/adjust/`, data)
  );

export const doAdjustAccount =
  (clientCode: string, data: AdjustAccountBodyData) => async (dispatch) => {
    dispatch({ type: 'accounts/adjust/started' });

    try {
      await requestAdjustAccount(clientCode, data);

      dispatch({
        type: 'accounts/adjust/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'accounts/adjust/finished' },
          e,
          `Failed to adjust account.`
        )
      );
    }
  };

export interface TransferAdjustAccountBodyData {
  source: string;
  target: string;
  post_date: string;
  amount: string;
  description: string;
  notes?: string;
  type: AdjustmentTypes;
}

export const requestTransferAdjustAccount = async (
  data: AdjustAccountBodyData
) =>
  confirmInDangerousDev(
    async () => await api.post(`/xact/adjusting_transfer/`, data)
  );

export const doTransferAdjustAccount =
  (data: AdjustAccountBodyData) => async (dispatch) => {
    dispatch({ type: 'accounts/transfer-adjust/started' });

    try {
      await requestTransferAdjustAccount(data);

      dispatch({
        type: 'accounts/transfer-adjust/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'accounts/transfer-adjust/finished' },
          e,
          `Failed to do transfer adjustment account.`
        )
      );
    }
  };
// #endregion
